// The signed-in person (GET /me), read once and refreshed after sign-in,
// sign-up and sign-out.
import { createContext, useCallback, useContext, useEffect, useState, type ReactNode } from 'react';
import { ApiError } from './api-client';
import { useApi } from './api-context';

export interface Me {
  isSignUpPending: boolean;
  email: string | null;
  accountId: string | null;
  firstName: string | null;
  lastName: string | null;
  timeZone: string | null;
  isAdmin: boolean;
  coachStatus: string;
  coachProfileId: string | null;
  suggestedFirstName: string | null;
  suggestedLastName: string | null;
}

interface CallerValue {
  me: Me | null;
  isLoading: boolean;
  // GET /me got no usable answer (no connection, or a server failure). The
  // person may well be signed in, so they are not treated as signed out.
  isUnreachable: boolean;
  refresh: () => Promise<Me | null>;
  setMe: (me: Me | null) => void;
}

// Only these answers mean "not signed in" (or not allowed).
const SIGNED_OUT_STATUSES = new Set([401, 403]);

const CallerContext = createContext<CallerValue | null>(null);

// initialMe is given by tests; the app reads GET /me instead.
export function CallerProvider(props: { initialMe?: Me | null; children: ReactNode }) {
  const api = useApi();
  const [me, setMe] = useState<Me | null>(props.initialMe ?? null);
  const [isLoading, setIsLoading] = useState(props.initialMe === undefined);
  const [isUnreachable, setIsUnreachable] = useState(false);

  const refresh = useCallback(async () => {
    try {
      const next = await api.get<Me>('/api/v1/me');
      setMe(next);
      setIsUnreachable(false);
      return next;
    } catch (error) {
      setMe(null);
      // A real 401 or 403 means signed out. Anything else, a dropped
      // connection included, is shown as the connection being down.
      setIsUnreachable(!(error instanceof ApiError && SIGNED_OUT_STATUSES.has(error.status)));
      return null;
    } finally {
      setIsLoading(false);
    }
  }, [api]);

  useEffect(() => {
    if (props.initialMe === undefined) void refresh();
  }, [props.initialMe, refresh]);

  return (
    <CallerContext.Provider value={{ me, isLoading, isUnreachable, refresh, setMe }}>
      {props.children}
    </CallerContext.Provider>
  );
}

export function useCaller(): CallerValue {
  const value = useContext(CallerContext);
  if (!value) throw new Error('CallerProvider is missing');
  return value;
}
